import { css } from '@emotion/react';
import { tokens, variables, shadow, applyFont } from '@domain-group/fe-brary';
import { media } from '../../styles/breakpoints-2026';

const { spacing, colors, shadows, borderRadius, typography } = tokens;

const CARD_RADIUS = '24px';
const AVATAR_SIZE = 56;

const CONTENT_MIN_HEIGHT = '56px';

export const sidebarCard = css`
  display: none;

  ${media('>=desktop')} {
    display: flex;
    flex-direction: column;
    gap: ${spacing.m};
    padding: ${spacing.m};
    border: 1px solid ${colors.neutral.trim.default};
    border-radius: ${CARD_RADIUS};
    box-shadow: ${shadow(shadows.elevation3)};
    background: ${colors.neutral.surface.default};
    position: sticky;
    top: ${spacing.l};
  }
`;

// gap is conditional on revealed - flexbox gap applies between adjacent
// items even when one is collapsed to 0x0, which would otherwise leave a
// phantom gap in front of the heading while the avatar is hidden.
export const identity = (revealed: boolean) => css`
  display: flex;
  align-items: center;
  gap: ${revealed ? spacing.s : '0px'};
  overflow: hidden;
  flex: 1;
  min-width: 0;
  transition: gap 300ms ease;

  ${media('>=tablet', '<desktop')} {
    min-height: ${CONTENT_MIN_HEIGHT};
  }
`;

export const avatarWrapper = (revealed: boolean) => css`
  width: ${revealed ? AVATAR_SIZE : 0}px;
  height: ${revealed ? AVATAR_SIZE : 0}px;
  flex-shrink: 0;
  overflow: hidden;
  transition:
    width 300ms ease,
    height 300ms ease;
`;

export const avatarImage = (revealed: boolean) => css`
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  border-radius: ${borderRadius.inner};
  transform: scale(${revealed ? 1 : 0.4});
  opacity: ${revealed ? 1 : 0};
  transition:
    transform 300ms ease,
    opacity 300ms ease;
`;

export const avatarPlaceholder = (revealed: boolean) => css`
  position: relative;
  width: ${revealed ? AVATAR_SIZE : 0}px;
  height: ${revealed ? AVATAR_SIZE : 0}px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: ${borderRadius.inner};
  background: ${colors.neutral.subdued.default};
  transition:
    width 300ms ease,
    height 300ms ease;
`;

// the icon overflows the bottom edge slightly (-2.27%) to replicate a cropped headshot
export const avatarPlaceholderIcon = css`
  position: absolute;
  top: 18.18%;
  right: 9%;
  bottom: -2.27%;
  left: 9.4%;
  width: auto;
  height: auto;
  color: ${colors.secondary.trim.hover};
`;

export const identityText = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

export const heading = css`
  ${applyFont(typography.body)}
  color: ${colors.neutral.heavy.default};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const jobTitleWrapper = (revealed: boolean) => css`
  display: grid;
  grid-template-rows: ${revealed ? '1fr' : '0fr'};
  overflow: hidden;
  transition: grid-template-rows 300ms ease;
`;

export const jobTitle = css`
  ${applyFont(typography.caption)}
  color: ${colors.neutral.medium.default};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ctaGroup = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const floatingBarMobileOuter = css`
  display: none;

  ${media('<tablet')} {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${colors.neutral.surface.default};
    border-top: 1px solid ${colors.neutral.trim.default};
    border-radius: ${CARD_RADIUS} ${CARD_RADIUS} 0 0;
    box-shadow: ${shadow(shadows.elevation3)};
    z-index: ${variables.global.layerFloating};
    padding-bottom: ${spacing.xl};
  }
`;

export const floatingBarMobileInner = css`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.m};
  width: 100%;
`;

export const floatingBarTablet = css`
  display: none;

  ${media('>=tablet', '<desktop')} {
    display: flex;
    justify-content: center;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: ${variables.global.layerFloating};
    padding: ${spacing.m} ${spacing.m} ${spacing.xl};
  }
`;

export const floatingBarTabletCard = css`
  display: flex;
  align-items: center;
  gap: ${spacing.s};
  width: 100%;
  max-width: 704px;
  padding: ${spacing.m};
  border: 1px solid ${colors.neutral.trim.default};
  border-radius: ${CARD_RADIUS};
  box-shadow: ${shadow(shadows.elevation3)};
  background: ${colors.neutral.surface.default};
`;

export const floatingBarCtaGroup = css`
  display: flex;
  flex-shrink: 0;
  gap: ${spacing.xs};
`;

// The two mobile CTAs should be equal width. white-space:nowrap on
// styles.button gives each button a min-content floor equal to its full
// label width, so the longer label otherwise claims more space under equal
// flex-grow - override just here (mobile only; tablet keeps natural,
// unequal widths per design) rather than on the shared button style.
export const floatingBarMobileCtaGroup = css`
  display: flex;
  flex: 1;
  gap: ${spacing.xs};

  > a,
  > button {
    min-width: 0;
    // LinkButton's default (regular) padding is 8px 26px - too wide for the
    // "Request appraisal" label at narrow mobile widths, clipping the text.
    padding: 8px ${spacing.s};
  }
`;

export const button = css`
  flex: 1;
  white-space: nowrap;

  ${media('<tablet')} {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

export const labelledButtonWrapper = css`
  ${media('<tablet')} {
    display: none;
  }
`;

export const iconOnlyButtonWrapper = css`
  display: none;

  ${media('<tablet')} {
    display: block;
  }
`;
